import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import { RequiredRule } from "devextreme-react/tree-list";
import { useEffect, useRef } from "react";
import {
  Gantt,
  StripLine,
  Tasks,
  Resources,
  ResourceAssignments,
  Toolbar,
  Editing,
  Column,
  Item,
  ContextMenu,
} from "devextreme-react/gantt";
import {
  Chart,
  Series,
  CommonSeriesSettings,
  Legend,
  ValueAxis,
  Tooltip,
  Format,
  AxisLabel,
  ArgumentAxis,
} from "devextreme-react/chart";
import PieChart, { Label, Connector } from "devextreme-react/pie-chart";
import { Popup as MyPopup, ToolbarItem } from "devextreme-react/popup";
import { Form, GroupItem } from "devextreme-react/form";
import DropDownBox from "devextreme-react/drop-down-box";
import TreeView from "devextreme-react/tree-view";
import LoadingSpinnerModal from "../../Components/LoadingSpinnerModal";
import { apiBaseURL } from "../../Utils/ProjectConstants";
import Modal from "../../Components/Modal";
import useFetch from "../../Hooks/useFetch";
import ActionNotifications from "../../Components/ActionNotifications";
import ErrorMessage from "../../Components/ErrorMessage";
import { checkRoles } from "../../Utils/helper";
import useFetchSupervisors from "../../Hooks/dataFetcher/useFetchSupervisors";
import useFetchWorkPlans from "../../Hooks/dataFetcher/useFetchWorkPlans";
import useFetchTasks from "../../Hooks/dataFetcher/useFetchTasks";
import ProjectTreeList from "./Components/projectTreeList";
import "./styles.css";

export default function ProjectPlanPage() {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfTomorrow = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1
  );

  const navigate = useNavigate(),
    params = useParams(),
    fetch = useFetch(navigate),
    [notifyResponse, setResponse] = useState(null),
    [state, setState] = useState({
      showSpinnerModal: false,
      showTaskDetails: false,
      selectedTask: {},
      selectedName: "",
      selectedId: "",
      scale: "months",
      upcoming: false,
      missed: false,
      billingCode: params.projectId,
      allTasks: [],
      supervisorIds: [],
      tasks: [],
    });
  const {
    allSupervisors,
    error: supervisorError,
    isLoading: supervisorsIsLoading,
  } = useFetchSupervisors();
  const supervisors = allSupervisors?.map((x) => ({
    id: x.id,
    text: x.fullName,
  }));
  const {
    allTasks,
    error: taskError,
    isLoading: taskIsLoading,
  } = useFetchTasks(state.billingCode);
  const {
    projectName,
    allProjects,
    resourceAssignments,
    tasks,
    pastDueProjects,
    upcomingProjects,
    isLoading: workPlansIsLoading,
    error,
  } = useFetchWorkPlans(params.projectId);
  console.log("CHECK THIS", tasks);
  // put this right under your current `const taskz = [...]`

  const levelPalette = [
    "#1565C0", // L1 – Blue
    "#00897B", // L2 – Teal
    "#F9A825", // L3 – Amber/Gold
    "#8E24AA", // L4 – Purple
    "#E53935", // L5+ – Coral/Red
  ];
  const onCustomCommand = (e) => {
    if (e.name === "addHours") {
      // e.key should be the task id / key
      console.log("Add Hours for task:", e);

      // Example: look up the task and open your modal
      const task = allProjects.find(
        (x) => x.id === e.key || x.billingCode === e.key
      );

      if (task) {
        setState((prev) => ({
          ...prev,
          showTaskDetails: true, // or your own "add hours" modal flag
          selectedTask: task,
        }));
      }
    }
  };
  const handleGanttCustomCommand = (e) => {
    console.log("onCustomCommand fired:", e);

    // if (e.name !== "addHours") return;

    // // e.key is the task key used by Gantt
    // const task = tasksWithLevelColors.find((t) => t.id === e.key);
    // if (!task) {
    //   console.warn("No task found for key", e.key);
    //   return;
    // }

    navigate("/hours", {
      // state: {
      //   billingCode: task.id,
      //   projectId: params.projectId,
      // },
    });
  };

  const tasksWithLevelColors = React.useMemo(() => {
    if (!Array.isArray(tasks)) return [];

    const byId = new Map(tasks.map((t) => [t.id, t]));

    const getLevel = (t) => {
      // Prefer dotted billing codes like "25.1.3" → 3
      if (t.billingCode) {
        const s = String(t.billingCode).trim();
        if (s.length) return s.split(".").filter(Boolean).length || 1;
      }
      // Fallback: walk the parent chain
      let lvl = 1,
        p = t.parentId;
      while (p && byId.has(p)) {
        lvl += 1;
        p = byId.get(p).parentId;
      }
      return lvl;
    };

    return tasks.map((t) => {
      const lvl = getLevel(t);
      const color = levelPalette[Math.min(lvl - 1, levelPalette.length - 1)];
      return { ...t, taskColor: color, _level: lvl };
    });
  }, [tasks]);

  const [gantt, setGantt] = useState(null);
  const [isGanttReady, setIsGanttReady] = useState(false);
  const ganttRef = useRef(null);
  useEffect(() => {
    if (!gantt || !isGanttReady || !tasks?.length) return;

    const id = setTimeout(() => {
      try {
        const anchor = new Date();
        switch (state.scale) {
          case "weeks":
            anchor.setDate(anchor.getDate() - 21); // ~3 weeks
            break;
          case "months":
            anchor.setMonth(anchor.getMonth() - 3); // ~3 months
            break;
          case "quarters":
            anchor.setMonth(anchor.getMonth() - 6); // a bit wider for quarters
            break;
          default:
            anchor.setMonth(anchor.getMonth() - 3);
        }
        gantt.scrollToDate(anchor);
      } catch {
        setTimeout(() => gantt.scrollToDate(new Date()), 0);
      }
    }, 0);

    return () => clearTimeout(id);
  }, [gantt, isGanttReady, tasks, state.scale]);

  const fetchSuccessful = (response) => {
    setResponse(response);
    return (
      response?.success &&
      response?.data?.statusCode &&
      response?.success !== "Invalid Model"
    );
  };

  const allowEditing = async (e) => {
    let response = null;

    const editBody = {
      taskName: e.taskName,
      allocatedEngHours: e.allocatedEngHours,
      allocatedLabHours: e.allocatedLabHours,
      startDate: e.startDate,
      endDate: e.endDate,
      supervisorIds: e.supervisorIds,
      completedPercentage: e.completedPercentage,
      description: e.description,
      headId: e.headId,
      billingCode: e.billingCode,
      remarks: "Edit Task",
      id: e.id,
    };
    response = await fetch.put(`${apiBaseURL}/api/projectwo`, editBody);

    console.log(response);
    setState({ ...state, showSpinner: true });
    if (fetchSuccessful(response)) {
      setState({
        ...state,
        showTaskDetails: false,
      });
    }
    setState({ ...state, showSpinner: false });
  };

  const onShowInfoModel = () => {
    if (state.showTaskDetails) {
      return (
        <>
          <MyPopup
            visible={state.showTaskDetails}
            onHiding={() =>
              setState({
                ...state,
                showTaskDetails: false,
                selectedTask: allProjects.find(
                  (x) => state.selectedTask.billingCode === x.billingCode
                ),
              })
            }
            hideOnOutsideClick={true}
            title="Edit Task"
            width="30%"
            height="auto"
            resizeEnabled={true}
          >
            <ToolbarItem
              widget="dxButton"
              toolbar="bottom"
              location="after"
              options={
                /*state.onUpdate ?*/ {
                  text: "Update",
                  useSubmitBehavior: true,
                  onClick: () => {
                    allowEditing(state.selectedTask);
                  },
                }
              }
            />
            <Form
              formData={state.selectedTask}
              showValidationSummary={true}
              validationGroup="uploadHours"
            >
              <GroupItem colCount={2}>
                <Item
                  dataField="taskName"
                  editorType="dxTextBox"
                  editorOptions={{}}
                  colSpan={2}
                >
                  <RequiredRule message="Name is required" />
                </Item>
                <Item
                  dataField="startDate"
                  editorType="dxDateBox"
                  editorOptions={{
                    width: "100%",
                    type: "date",
                    displayFormat: "shortdate",
                    showClearButton: true,
                    useMaskBehavior: true,
                    placeholder: "mm/dd/yyyy",
                  }}
                  colSpan={1}
                >
                  <RequiredRule message="Start date is required" />
                </Item>
                <Item
                  dataField="endDate"
                  editorType="dxDateBox"
                  editorOptions={{
                    width: "100%",
                    type: "date",
                    displayFormat: "shortdate",
                    showClearButton: true,
                    useMaskBehavior: true,
                    placeholder: "mm/dd/yyyy",
                  }}
                  colSpan={1}
                >
                  <RequiredRule message="End date is required" />
                </Item>
                <Item
                  dataField="supervisorIds"
                  editorType="dxTagBox"
                  editorOptions={{
                    showSelectionControls: true,
                    items: supervisors,
                    valueExpr: "id",
                    displayExpr: "text",
                    searchEnabled: true,
                  }}
                  colSpan={2}
                >
                  <Label text="Assigned to" />
                  <RequiredRule message="Assignment is required" />
                </Item>
                <Item
                  dataField="allocatedEngHours"
                  editorType="dxNumberBox"
                  editorOptions={{}}
                  colSpan={2}
                />
                <Item
                  dataField="completedPercentage"
                  editorType="dxSlider"
                  editorOptions={{
                    min: 0,
                    max: 100,
                    tooltip: {
                      enabled: true,
                      showMode: "always",
                      position: "bottom",
                    },
                  }}
                  colSpan={2}
                >
                  <Tooltip
                    enabled={true}
                    showMode="always"
                    position="bottom" /*format={format} */
                  />
                  <RequiredRule message="Percentage is required" />
                </Item>
              </GroupItem>
            </Form>
          </MyPopup>
        </>
      );
    } else if (state.upcoming) {
      const showUpcoming = upcomingProjects?.workPlans?.map(
        (x) => ` ${x.taskName}`
      );
      return (
        <Modal
          modalHeaderTitle={`Upcoming Deadlines`}
          modalBody={`Upcoming Deadlines: ${showUpcoming}`}
          onModalCloseClick={() => setState({ ...state, upcoming: false })}
          onModalFooterBtnClick={() => setState({ ...state, upcoming: false })}
          modalFooterBtnText="Cancel"
          colorBtnClass="redBG ltGray"
        />
      );
    } else if (state.missed) {
      const showMissed = pastDueProjects?.workPlans?.map(
        (x) => ` ${x.taskName}`
      );
      return (
        <Modal
          modalHeaderTitle={`Past Due`}
          modalBody={`Past Due: ${showMissed}`}
          onModalCloseClick={() => setState({ ...state, missed: false })}
          onModalFooterBtnClick={() => setState({ ...state, missed: false })}
          modalFooterBtnText="Cancel"
          colorBtnClass="redBG ltGray"
        />
      );
    } else {
      return null;
    }
  };

  const getProjectDetails = () => {
    //const pageSizes = [10, 25, 50, 100];

    const customizeText = (e) => {
      console.log(e);
      return `${e.valueText} (${e.percentText})`;
    };
    //const onDragChange = async (e) => {
    //    console.log(e)
    //    const visibleRows = e.component.getVisibleRows();
    //    const sourceNode = e.component.getNodeByKey(e.itemData.billingCode);
    //    let targetNode = visibleRows[e.toIndex].node;
    //    console.log(sourceNode)
    //    console.log(visibleRows)
    //    console.log(targetNode)

    //    while (targetNode && targetNode.data) {
    //        if (targetNode.data.billingCode === sourceNode.data.billingCode) {
    //            e.cancel = true;
    //            break;
    //        }
    //        console.log(e.fromIndex)
    //        console.log(e.toIndex)
    //        let targetIndex = parseInt(targetNode.key) - 1
    //        let targetIndexx = targetIndex.toString()
    //        console.log(targetIndexx)
    //        //const supervisorIds = editType.data.supervisors.map((x) => ({ value: x.id, label: `${x.firstName} ${x.lastName}` }))

    //        const editBody = {
    //            //billingCode: sourceNode.data.billingCode,
    //            billingCode: targetIndexx,
    //            taskName: sourceNode.data.taskName,
    //            allocatedEngHours: sourceNode.data.allocatedEngHours,
    //            allocatedLabHours: sourceNode.data.allocatedLabHours,
    //            startDate: sourceNode.data.startDate,
    //            endDate: sourceNode.data.endDate,
    //            //supervisorIds: supervisorIds,
    //            supervisorIds: ["dbe694e9-8599-41ca-86b7-1532d107293c"],        //hardcoded
    //            projectType: "Federal",         //hardcoded
    //            description: sourceNode.data.description,
    //            headId: sourceNode.data.headId,
    //            id: sourceNode.data.id
    //        }
    //        let response = await fetch.put(`${apiBaseURL}/api/project`, editBody);
    //        console.log(response)
    //        targetNode = targetNode.parent;
    //    }
    //}

    //const onReorder = async (e) => {
    //    const visibleRows = e.component.getVisibleRows();
    //    let sourceData = e.itemData;
    //    const sourceIndex = e.fromIndex;

    //    if (e.dropInsideItem) {
    //        sourceData = { ...sourceData, headId: visibleRows[e.toIndex].key };

    //        const editBody = {
    //            billingCode: sourceData.data.billingCode,
    //            //billingCode: targetIndexx,
    //            taskName: sourceData.data.taskName,
    //            allocatedEngHours: sourceData.data.allocatedEngHours,
    //            allocatedLabHours: sourceData.data.allocatedLabHours,
    //            startDate: sourceData.data.startDate,
    //            endDate: sourceData.data.endDate,
    //            //supervisorIds: supervisorIds,
    //            supervisorIds: ["dbe694e9-8599-41ca-86b7-1532d107293c"],        //hardcoded
    //            projectType: "Federal",         //hardcoded
    //            description: sourceData.data.description,
    //            headId: e.toIndex,
    //            id: sourceData.data.id
    //        }
    //        let response = await fetch.put(`${apiBaseURL}/api/project`, editBody);

    //    } else {
    //        const toIndex = e.fromIndex > e.toIndex ? e.toIndex - 1 : e.toIndex;
    //        let targetData = toIndex >= 0 ? visibleRows[toIndex].node.data : null;
    //        console.log(targetData)
    //        if (targetData && e.component.isRowExpanded(targetData.id)) {
    //            sourceData = { ...sourceData, headId: targetData.id };
    //            targetData = null;
    //        } else {
    //            const head = targetData ? targetData.headId : "";
    //            if (sourceData.headId !== head) {
    //                sourceData = { ...sourceData, headId: head };
    //            }
    //        }

    //        //employees = [...employees.slice(0, sourceIndex), ...employees.slice(sourceIndex + 1)];

    //        const targetIndex = e.toIndex + 1;
    //        //employees = [...employees.slice(0, targetIndex), sourceData, ...employees.slice(targetIndex)];
    //    }
    //}

    const onTaskUpdating = async (e) => {
      const selectedTaskObj = allProjects.find((x) => e.key === x.billingCode);
      setState({ ...state, selectedTask: selectedTaskObj }); //remove?
      const body = {
        taskName: selectedTaskObj.taskName,
        allocatedEngHours: selectedTaskObj.allocatedEngHours,
        allocatedLabHours: selectedTaskObj.allocatedLabHours,
        startDate: e.newValues.start
          ? e.newValues.start
          : selectedTaskObj.startDate,
        endDate: e.newValues.end ? e.newValues.end : selectedTaskObj.endDate,
        supervisorIds: selectedTaskObj.supervisorIds,
        completedPercentage: e.newValues.progress
          ? e.newValues.progress
          : selectedTaskObj.completedPercentage,
        description: selectedTaskObj.description,
        headId: selectedTaskObj.headId,
        billingCode: selectedTaskObj.billingCode,
        remarks: "Edit Task",
        id: selectedTaskObj.id,
      };
      const response = await fetch.put(`${apiBaseURL}/api/projectwo`, body);
      setState({ ...state, showSpinner: true });
      if (fetchSuccessful(response)) {
      }
      setState({ ...state, showSpinner: false });
    };

    const onTaskEditDialogShowing = (e) => {
      const selectedTaskObj = allProjects.find((x) => e.key === x.billingCode);
      setState({
        ...state,
        showTaskDetails: true,
        selectedTask: selectedTaskObj,
      });
      e.cancel = true;
    };

    const syncTreeViewSelection = (e) => {
      setState({
        ...state,
        billingCode: e.value !== null ? e.value : params.projectId,
      });
    };
    const onTreeBoxOpened = (e) => {
      if (e.name === "opened") {
        return e.value;
      }
    };
    const onTreeItemClick = (e) => {
      return false;
    };
    const treeViewItemSelectionChanged = (e) => {
      console.log(e);
      if (e.node.children.length > 0) {
        setState({ ...state, billingCode: e.node.key });
      }
    };
    const treeViewRender = (e) => {
      return (
        <TreeView
          dataSource={allProjects}
          //ref={(ref) => { this.treeView = ref; }}
          dataStructure="plain"
          rootValue={params.projectId}
          keyExpr="billingCode"
          parentIdExpr="headId"
          selectionMode="single"
          //showCheckBoxesMode={true}
          displayExpr="billingCode"
          selectByClick={true}
          onItemClick={onTreeItemClick}
          onItemSelectionChanged={treeViewItemSelectionChanged}
        />
      );
    };

    //const onExporting = (e) => {
    //    console.log(e)
    //    const workbook = new Workbook();
    //    const worksheet = workbook.addWorksheet('Project_Work_Plan');

    //    //let element = document.getElementById("TreeList")
    //    //let instance = DevExpress.ui.dxTreeList.getInstance(element);
    //    //console.log(instance)
    //    exportDataGrid({
    //        component: e.component,
    //        worksheet,
    //    }).then(() => {
    //        workbook.xlsx.writeBuffer().then((buffer) => {
    //            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Project_Work_Plan.xlsx');
    //        });
    //    });
    //    e.cancel = true;
    //}

    return (
      <div className="container-fluid g-0">
        <div className="row">
          <div className="row">
            <div className="contentBox col text-center white">
              <div>
                <h5>Total Deadlines</h5>
                <h3>{tasks.length}</h3>
              </div>
            </div>
            <div className="col-1"></div>
            <div
              className="contentBox col text-center white" /*onClick={() => setState({ ...state, upcoming: true })}*/
            >
              <div>
                <h5>Upcoming</h5>
                <h3>{upcomingProjects?.count}</h3>
              </div>
            </div>
            <div className="col-1"></div>
            <div
              className="contentBox col text-center white" /*onClick={() => setState({ ...state, missed: true })}*/
            >
              <div>
                <h5>Past Due</h5>
                <h3>{pastDueProjects?.count}</h3>
              </div>
            </div>
          </div>
          <div id="filler" />
          {checkRoles(2) && (
            <div className="contentBox">
              <div className="col-3">
                <DropDownBox
                  value={state.billingCode}
                  valueExpr="billingCode"
                  displayExpr="billingCode"
                  placeholder={params.projectId}
                  showClearButton={true}
                  dataSource={allProjects}
                  onValueChanged={syncTreeViewSelection}
                  onOptionChanged={onTreeBoxOpened}
                  contentRender={treeViewRender}
                />
              </div>
              <br />
              <div className="row">
                <div className="col-5">
                  <PieChart
                    id="pie"
                    dataSource={allTasks?.areas}
                    palette="Bright"
                    title={{
                      text: "On-Time Performance",
                      font: { color: "white" },
                    }}
                    height={300}
                  >
                    <Legend
                      orientation="horizontal"
                      itemTextPosition="right"
                      horizontalAlignment="center"
                      verticalAlignment="bottom"
                      rowCount={2}
                      font={{ color: "white" }}
                    />
                    <Series argumentField="performance" valueField="area">
                      <Label
                        visible={true}
                        font={{ color: "black", weight: "bold" }}
                      >
                        <Connector
                          visible={true}
                          customizeText={customizeText}
                        />
                      </Label>
                    </Series>
                  </PieChart>
                </div>
                <div className="col-6">
                  <Chart
                    id="chart"
                    title={{
                      text: "Planned vs Actual Hours per task",
                      font: { color: "white" },
                    }}
                    dataSource={allTasks?.workPlan}
                    height={300}
                  >
                    <CommonSeriesSettings
                      argumentField="billingCode"
                      type="bar"
                      hoverMode="allArgumentPoints"
                      selectionMode="allArgumentPoints"
                    >
                      <Label
                        visible={true}
                        position="outside"
                        font={{ color: "black", weight: "bold" }}
                      >
                        <Format type="fixedPoint" precision={0} />
                      </Label>
                    </CommonSeriesSettings>
                    <Series valueField="allocatedEngHours" name="Planned Eng" />
                    <Series valueField="actualEngHours" name="Actual Eng" />
                    <Series valueField="allocatedLabHours" name="Planned Lab" />
                    <Series valueField="actualLabHours" name="Actual Lab" />
                    <ArgumentAxis>
                      <AxisLabel
                        // adjust rotation/overlap if long labels
                        rotationAngle={0}
                        overlappingBehavior="stagger"
                        font={{ color: "#ffffff" }}
                      />
                    </ArgumentAxis>
                    <ValueAxis axisDivisionFactor={50}>
                      <AxisLabel font={{ color: "#ffffff" }} />
                    </ValueAxis>
                    <Legend
                      verticalAlignment="bottom"
                      horizontalAlignment="center"
                      font={{ color: "white" }}
                    ></Legend>
                  </Chart>
                </div>
              </div>
            </div>
          )}
          <div id="filler"></div>
          {tasks ? (
            <div className="container-fluid">
              <div className="row">
                <ProjectTreeList
                  projectId={params.projectId}
                  fetch={fetch}
                  fetchSuccessful={fetchSuccessful}
                />
              </div>
              <div id="filler"></div>
              <div className="contentBox row">
                <div>
                  <Gantt
                    onInitialized={(e) => setGantt(e.component)}
                    onContentReady={() => setIsGanttReady(true)}
                    taskListWidth={600}
                    // taskContentRender={renderTaskContent}
                    scaleType={state.scale}
                    // scaleType="months"
                    height={Math.min(
                      1000,
                      Math.max(300, tasksWithLevelColors.length * 55)
                    )}
                    wordWrapEnabled={true}
                    allowSorting={true}
                    //onDependencyInserting={onDependencyInserting}
                    // onCustomCommand={onCustomCommand}
                    onCustomCommand={handleGanttCustomCommand}
                    onTaskUpdating={onTaskUpdating}
                    onTaskEditDialogShowing={onTaskEditDialogShowing}
                    showResources={false}
                  >
                    <ContextMenu>
                      <Item name="addHours" text="Add Hours" />
                    </ContextMenu>

                    <StripLine
                      start={startOfToday}
                      end={startOfTomorrow}
                      title="Today"
                      cssClass="current-time"
                    />
                    <Tasks
                      dataSource={tasksWithLevelColors}
                      colorExpr="taskColor"
                    />
                    {/*<Dependencies dataSource={state.dependencies} />*/}
                    <Resources dataSource={supervisors} />
                    <ResourceAssignments dataSource={resourceAssignments} />

                    <Toolbar elementAttr={{ class: "my-toolbar" }}>
                      <Item name="collapseAll" />
                      <Item name="expandAll" />
                      <Item name="separator" />
                      <Item
                        widget="dxButton"
                        location="before"
                        options={{
                          text: "Weeks",
                          onClick: () =>
                            setState((s) => ({ ...s, scale: "weeks" })),
                        }}
                      />
                      <Item
                        widget="dxButton"
                        location="before"
                        options={{
                          text: "Months",
                          onClick: () =>
                            setState((s) => ({ ...s, scale: "months" })),
                        }}
                      />
                      <Item
                        widget="dxButton"
                        location="before"
                        options={{
                          text: "Quarters",
                          onClick: () =>
                            setState((s) => ({ ...s, scale: "quarters" })),
                        }}
                      />
                    </Toolbar>
                    {/*<ScaleTypeRange*/}

                    <Column dataField="id" caption="Billing Code" width={160} />
                    <Column dataField="title" caption="Task" minWidth={200} />
                    <Column
                      dataField="start"
                      caption="Start Date"
                      dataType="date"
                      sortIndex={1}
                      sortOrder="asc"
                      width={110}
                    />
                    <Column
                      dataField="end"
                      caption="End Date"
                      dataType="date"
                      width={110}
                    />
                    <Editing enabled allowDependencyAdding={false} />
                  </Gantt>
                </div>
              </div>
            </div>
          ) : (
            <h5 className="py-3 text-center fw-normal">
              No task have been made. Contact supervisor.
            </h5>
          )}
          <div id="filler"></div>
          <div id="filler"></div>
        </div>
      </div>
    );
  };

  if (taskIsLoading || workPlansIsLoading || supervisorsIsLoading)
    return <LoadingSpinnerModal show />;

  return (
    <div className="px-lg-4 content">
      {onShowInfoModel()}
      {notifyResponse && <ActionNotifications response={notifyResponse} />}
      <LoadingSpinnerModal show={state.showSpinnerModal} />
      <div className="px-2 page-content">
        <div className="row my-4">
          <h2 className="white">{projectName}</h2>
        </div>
        <div id="link">
          <Link className="hoverLinkText gray" to="/projects">
            <h5 className="mb-3 white">&#x2190; Projects</h5>
          </Link>
        </div>
        {!error ? (
          getProjectDetails()
        ) : (
          <div className="boxCotainerWrapper my-3">
            <ErrorMessage />
          </div>
        )}
      </div>
    </div>
  );
}
